import * as React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/core/components/ui/button";
import { Input } from "@/core/components/ui/input";
import { Checkbox } from "@/core/components/ui/checkbox";
import type { InvalidEmail } from "../utils/parseEmailsFromSpreadsheet";
import {
  validateSingleEmail,
  autocorrectEmailToDomain,
} from "../utils/parseEmailsFromSpreadsheet";
import {
  X,
  Check,
  Minus,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  Sparkles,
  UserRoundMinus,
  UserRoundPlus,
} from "lucide-react";
import { cn } from "@/core/utils";
import { useActionsTrack } from "@/core/services/menu-analytics";

const REASON_KEYS: Record<InvalidEmail["reason"], string> = {
  formato_invalido: "chatbot-import-reason-format",
  dominio_invalido: "chatbot-import-reason-domain",
  duplicado: "chatbot-import-reason-duplicate",
};

export interface EmailValidationPanelProps {
  validEmails: string[];
  invalidEmails: InvalidEmail[];
  hasEmailColumn: boolean;
  totalRows: number;
  allowedDomains: string[];
  confirmed?: boolean;
  onConfirm: (validEmails: string[], action: "remove" | "invite") => void;
  onCancel: () => void;
}

type InvalidWithId = InvalidEmail & { id: number };

export function EmailValidationPanel({
  validEmails: initialValidEmails,
  invalidEmails: initialInvalidEmails,
  hasEmailColumn,
  allowedDomains = [],
  confirmed = false,
  onConfirm,
  onCancel,
}: EmailValidationPanelProps) {
  const [t] = useTranslation(["core"]);
  const { onActionClick } = useActionsTrack();
  const [validEmails, setValidEmails] = React.useState<string[]>([]);
  const [invalidEmails, setInvalidEmails] = React.useState<InvalidWithId[]>([]);
  const nextIdRef = React.useRef(0);

  const hasInvalid = initialInvalidEmails.length > 0;
  const onlyValid = initialValidEmails.length > 0 && !hasInvalid;
  const [showValid, setShowValid] = React.useState(onlyValid);
  const [showInvalid, setShowInvalid] = React.useState(hasInvalid);

  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());
  const [selectedValidEmails, setSelectedValidEmails] = React.useState<
    Set<string>
  >(new Set());
  const [validEditMode, setValidEditMode] = React.useState(false);
  const [validEditValues, setValidEditValues] = React.useState<
    Record<string, string>
  >({});
  const [editingSubsection, setEditingSubsection] = React.useState<
    InvalidEmail["reason"] | null
  >(null);
  const [editValues, setEditValues] = React.useState<Record<number, string>>(
    {},
  );
  const [showValidNoSelectionWarning, setShowValidNoSelectionWarning] =
    React.useState(false);

  React.useEffect(() => {
    setValidEmails([...initialValidEmails]);
    setInvalidEmails(
      initialInvalidEmails.map((e) => ({ ...e, id: nextIdRef.current++ })),
    );
    setSelectedIds(new Set());
    setSelectedValidEmails(new Set());
    setValidEditMode(false);
    setValidEditValues({});
    setEditingSubsection(null);
    setEditValues({});
    setShowValidNoSelectionWarning(false);
  }, [initialValidEmails, initialInvalidEmails]);

  React.useEffect(() => {
    if (selectedValidEmails.size > 0) setShowValidNoSelectionWarning(false);
  }, [selectedValidEmails.size]);

  const formatErrors = invalidEmails.filter(
    (e) => e.reason === "formato_invalido",
  );
  const domainErrors = invalidEmails.filter(
    (e) => e.reason === "dominio_invalido",
  );
  const duplicateErrors = invalidEmails.filter((e) => e.reason === "duplicado");

  const lastInvalidReason: InvalidEmail["reason"] | null =
    domainErrors.length > 0
      ? "dominio_invalido"
      : formatErrors.length > 0
        ? "formato_invalido"
        : duplicateErrors.length > 0
          ? "duplicado"
          : null;

  const toggleSelectAll = (ids: number[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const hasAnySelected = ids.some((id) => next.has(id));
      if (hasAnySelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleSelectOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isSubsectionAllSelected = (items: InvalidWithId[]) => {
    if (items.length === 0) return false;
    return items.every((e) => selectedIds.has(e.id));
  };

  const toggleSelectAllValid = () => {
    setSelectedValidEmails((prev) => {
      if (validEmails.length === 0) return prev;
      if (prev.size > 0) return new Set<string>();
      return new Set(validEmails);
    });
  };

  const toggleSelectOneValid = (email: string) => {
    setSelectedValidEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const handleConfirmValidEdits = () => {
    const toRemoveFromValid: string[] = [];
    const updates: { oldEmail: string; newEmail: string }[] = [];
    const toAddInvalid: InvalidWithId[] = [];
    const otherValid = validEmails.filter((e) => !selectedValidEmails.has(e));
    selectedValidEmails.forEach((email) => {
      const value = (validEditValues[email] ?? email).trim().toLowerCase();
      if (!value) {
        toRemoveFromValid.push(email);
        return;
      }
      const result = validateSingleEmail(value, allowedDomains, otherValid);
      if (result.valid) {
        if (value !== email) updates.push({ oldEmail: email, newEmail: value });
      } else {
        toRemoveFromValid.push(email);
        toAddInvalid.push({
          id: nextIdRef.current++,
          email: value,
          reason: result.reason,
        });
      }
    });
    setValidEmails((prev) => {
      let next = prev.filter((e) => !toRemoveFromValid.includes(e));
      updates.forEach(({ oldEmail, newEmail }) => {
        next = next.map((e) => (e === oldEmail ? newEmail : e));
        next = [...next].filter((e, i, arr) => arr.indexOf(e) === i);
      });
      return next;
    });
    if (toAddInvalid.length > 0) {
      setInvalidEmails((prev) => [...prev, ...toAddInvalid]);
    }
    setSelectedValidEmails(new Set());
    setValidEditMode(false);
    setValidEditValues((prev) => {
      const next = { ...prev };
      selectedValidEmails.forEach((e) => delete next[e]);
      return next;
    });
  };

  const handleRemoveValidSelected = () => {
    setValidEmails((prev) => prev.filter((e) => !selectedValidEmails.has(e)));
    setSelectedValidEmails(new Set());
    setValidEditMode(false);
    setValidEditValues((prev) => {
      const next = { ...prev };
      selectedValidEmails.forEach((e) => delete next[e]);
      return next;
    });
  };

  const handleConfirmOneValid = (email: string) => {
    const value = (validEditValues[email] ?? email).trim().toLowerCase();
    if (!value) {
      setValidEmails((prev) => prev.filter((e) => e !== email));
      setValidEditValues((prev) => {
        const next = { ...prev };
        delete next[email];
        return next;
      });
      setSelectedValidEmails((prev) => {
        const next = new Set(prev);
        next.delete(email);
        return next;
      });
      return;
    }
    const otherValid = validEmails.filter((e) => e !== email);
    const result = validateSingleEmail(value, allowedDomains, otherValid);
    if (result.valid) {
      setValidEmails((prev) =>
        prev
          .map((e) => (e === email ? value : e))
          .filter((e, i, arr) => arr.indexOf(e) === i),
      );
    } else {
      setValidEmails((prev) => prev.filter((e) => e !== email));
      setInvalidEmails((prev) => [
        ...prev,
        { id: nextIdRef.current++, email: value, reason: result.reason },
      ]);
    }
    setValidEditValues((prev) => {
      const next = { ...prev };
      delete next[email];
      return next;
    });
    setSelectedValidEmails((prev) => {
      const next = new Set(prev);
      next.delete(email);
      return next;
    });
  };

  const handleRemoveOneValid = (email: string) => {
    setValidEmails((prev) => prev.filter((e) => e !== email));
    setValidEditValues((prev) => {
      const next = { ...prev };
      delete next[email];
      return next;
    });
    setSelectedValidEmails((prev) => {
      const next = new Set(prev);
      next.delete(email);
      return next;
    });
  };

  const startEditSubsection = (reason: InvalidEmail["reason"]) => {
    const items =
      reason === "duplicado"
        ? duplicateErrors
        : reason === "formato_invalido"
          ? formatErrors
          : domainErrors;
    const values: Record<number, string> = {};
    items.forEach((item) => {
      if (selectedIds.has(item.id)) values[item.id] = item.email;
    });
    setEditValues((prev) => ({ ...prev, ...values }));
    setEditingSubsection(reason);
  };

  const saveEditSubsection = (reason: InvalidEmail["reason"]) => {
    const items =
      reason === "duplicado"
        ? duplicateErrors
        : reason === "formato_invalido"
          ? formatErrors
          : domainErrors;
    let nextValid = [...validEmails];
    const nextInvalid = invalidEmails.map((e) => ({ ...e }));
    items.forEach((item) => {
      const value = editValues[item.id]?.trim();
      if (value == null || value === "") return;
      const result = validateSingleEmail(value, allowedDomains, nextValid);
      const idx = nextInvalid.findIndex((x) => x.id === item.id);
      if (idx < 0) return;
      if (result.valid) {
        nextValid = [...nextValid, result.email].filter(
          (email, i, arr) => arr.indexOf(email) === i,
        );
        nextInvalid.splice(idx, 1);
      } else {
        nextInvalid[idx] = {
          ...nextInvalid[idx],
          email: value.toLowerCase(),
          reason: result.reason,
        };
      }
    });
    setValidEmails(nextValid);
    setInvalidEmails(nextInvalid);
    setEditingSubsection(null);
    setEditValues({});
  };

  const cancelEditSubsection = () => {
    setEditingSubsection(null);
    setEditValues({});
  };

  const saveEditOneInvalid = (id: number) => {
    const item = invalidEmails.find((e) => e.id === id);
    if (!item) return;
    const value = editValues[id]?.trim();
    if (value == null || value === "") {
      setInvalidEmails((prev) => prev.filter((e) => e.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setEditValues((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    const result = validateSingleEmail(value, allowedDomains, validEmails);
    if (result.valid) {
      setInvalidEmails((prev) => prev.filter((e) => e.id !== id));
      setValidEmails((prev) =>
        [...prev, result.email].filter((e, i, arr) => arr.indexOf(e) === i),
      );
    } else {
      setInvalidEmails((prev) =>
        prev.map((e) =>
          e.id === id
            ? { ...e, email: value.toLowerCase(), reason: result.reason }
            : e,
        ),
      );
    }
    setEditValues((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const cancelEditOneInvalid = (id: number) => {
    setEditValues((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleIgnoreSelected = (ids: number[]) => {
    setInvalidEmails((prev) => prev.filter((e) => !ids.includes(e.id)));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleAutocorrect = (reason: InvalidEmail["reason"], ids: number[]) => {
    if (reason === "duplicado") {
      handleIgnoreSelected(ids);
      return;
    }
    if (!allowedDomains[0]) return;
    // Só conta como "autocorrigir com IA" quando há domínio para corrigir: o ramo de
    // duplicados acima apenas ignora os itens, não é uma correção assistida.
    onActionClick(
      "AGENT_ACTION_DO_FOR_ME_AUTOCORRECT_MEMBER_IMPORT",
      "CHATBOT",
      {
        reason,
        emails_count: ids.length,
      },
    );
    const toRemove: number[] = [];
    const toUpdate: {
      id: number;
      email: string;
      newReason: InvalidEmail["reason"];
    }[] = [];
    const newValidEmails: string[] = [];
    invalidEmails.forEach((item) => {
      if (!ids.includes(item.id)) return;
      const corrected = autocorrectEmailToDomain(item.email, allowedDomains);
      const result = validateSingleEmail(
        corrected,
        allowedDomains,
        validEmails,
      );
      if (result.valid) {
        toRemove.push(item.id);
        newValidEmails.push(result.email);
      } else {
        toUpdate.push({
          id: item.id,
          email: corrected,
          newReason: result.reason,
        });
      }
    });
    if (newValidEmails.length > 0) {
      setValidEmails((prev) =>
        [...prev, ...newValidEmails].filter(
          (e, i, arr) => arr.indexOf(e) === i,
        ),
      );
    }
    setInvalidEmails((prev) =>
      prev
        .filter((e) => !toRemove.includes(e.id))
        .map((e) => {
          const u = toUpdate.find((u) => u.id === e.id);
          return u ? { ...e, email: u.email, reason: u.newReason } : e;
        }),
    );
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleAction = (action: "remove" | "invite") => {
    if (validEmails.length === 0) return;
    if (action === "invite") {
      onActionClick("AGENT_ACTION_DO_FOR_ME_ADD_EMPLOYEE", "CHATBOT", {
        emails_count: validEmails.length,
      });
    }
    onConfirm(validEmails, action);
  };

  if (!hasEmailColumn) {
    return (
      <div className="mx-4 w-full max-w-md overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm">
        <p className="text-sm text-muted-foreground">
          A planilha não contém uma coluna de email. Adicione uma coluna chamada
          &quot;email&quot;, &quot;e-mail&quot; ou &quot;E-mail&quot; e tente
          novamente.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 bg-card rounded-xl px-4">
      {/* Header */}
      <div className="flex items-center justify-between py-2">
        <h3 className="text-base font-semibold text-darkgray-900">
          {t("chatbot-import-panel-title")}
        </h3>
      </div>
      <div className="w-full overflow-hidden">
        <div className="">
          {/* Prontos para adicionar */}
          {validEmails.length > 0 && (
            <div className="mb-2 rounded-xl border border-border bg-card">
              <button
                type="button"
                disabled={confirmed}
                className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/30 disabled:cursor-default disabled:hover:bg-transparent"
                onClick={() => setShowValid(!showValid)}
                aria-expanded={showValid}
              >
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-success-500" />
                  <span className="text-base font-semibold text-darkgray-900">
                    {t("chatbot-import-ready-section")}
                  </span>
                  <span className="rounded-full bg-success-500 px-2.5 py-0.5 text-xs font-medium text-white tracking-[-0.072px] flex items-center justify-center">
                    {validEmails.length}
                  </span>
                </div>
                {showValid ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
              {showValid && (
                <div className="px-4 pb-3">
                  <SelectAllRow
                    totalCount={validEmails.length}
                    selectedCount={selectedValidEmails.size}
                    onToggle={toggleSelectAllValid}
                    disabled={confirmed}
                  />
                  <div className="max-h-[min(40vh,280px)] min-h-0 overflow-auto border rounded-b-xl">
                    <div className="overflow-hidden rounded-b-xl">
                      {validEmails.map((email, idx) => {
                        const isSelected = selectedValidEmails.has(email);
                        const isEditRow = isSelected && validEditMode;
                        const displayValue = validEditValues[email] ?? email;
                        return (
                          <div
                            key={email}
                            className={cn(
                              "flex items-center gap-2 px-4 py-3 transition-colors",
                              isSelected ? "bg-aiBlue-100" : "bg-white",
                              idx > 0 &&
                                !isSelected &&
                                !selectedValidEmails.has(validEmails[idx - 1])
                                ? "border-t border-border"
                                : idx > 0
                                  ? "border-t border-transparent"
                                  : "",
                            )}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() =>
                                toggleSelectOneValid(email)
                              }
                              disabled={confirmed}
                              className="shrink-0 rounded data-[state=checked]:bg-neutral-900 data-[state=checked]:border-neutral-900 data-[state=checked]:text-white"
                            />
                            {isEditRow ? (
                              <>
                                <Input
                                  value={displayValue}
                                  onChange={(e) =>
                                    setValidEditValues((prev) => ({
                                      ...prev,
                                      [email]: e.target.value,
                                    }))
                                  }
                                  className="h-8 flex-1 border border-border bg-white text-xs"
                                  disabled={confirmed}
                                />
                                <div className="flex shrink-0 items-center gap-0.5">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-foreground hover:bg-aiBlue-200"
                                    onClick={() => handleConfirmOneValid(email)}
                                    disabled={confirmed}
                                    aria-label={String(
                                      t("chatbot-import-confirm"),
                                    )}
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:bg-danger-500/10 hover:text-danger-500"
                                    onClick={() => {
                                      setValidEditValues((prev) => {
                                        const next = { ...prev };
                                        delete next[email];
                                        return next;
                                      });
                                      setSelectedValidEmails((prev) => {
                                        const next = new Set(prev);
                                        next.delete(email);
                                        return next;
                                      });
                                    }}
                                    disabled={confirmed}
                                    aria-label={String(
                                      t("chatbot-import-undo"),
                                    )}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <span className="min-w-0 flex-1 truncate text-sm text-darkgray-900">
                                {email}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {showValidNoSelectionWarning && (
                    <p className="mt-2 text-xs text-danger-500">
                      {t("chatbot-import-select-one")}
                    </p>
                  )}
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1 text-sm rounded-[10px] hover:bg-white text-aiBlue-500 hover:text-darkgray-900"
                      onClick={() => {
                        if (selectedValidEmails.size === 0) {
                          setShowValidNoSelectionWarning(true);
                          return;
                        }
                        if (validEditMode) {
                          handleConfirmValidEdits();
                        } else {
                          setValidEditMode(true);
                          const values: Record<string, string> = {};
                          selectedValidEmails.forEach((e) => {
                            values[e] = e;
                          });
                          setValidEditValues((prev) => ({
                            ...prev,
                            ...values,
                          }));
                        }
                      }}
                      disabled={confirmed}
                    >
                      <Pencil className="h-4 w-4" />
                      {t("chatbot-import-edit")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-aiBlue"
                      size="sm"
                      className="h-8 gap-1 text-sm rounded-[10px]"
                      onClick={() => {
                        if (selectedValidEmails.size === 0) {
                          setShowValidNoSelectionWarning(true);
                          return;
                        }
                        handleRemoveValidSelected();
                      }}
                      disabled={confirmed}
                    >
                      <Trash2 className="h-4 w-4" />
                      {t("chatbot-import-ignore")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Precisam de ajuste */}
          {invalidEmails.length > 0 && (
            <div className="mb-2 rounded-xl border border-border bg-card">
              <button
                type="button"
                disabled={confirmed}
                className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/30 disabled:cursor-default disabled:hover:bg-transparent"
                onClick={() => setShowInvalid(!showInvalid)}
                aria-expanded={showInvalid}
              >
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-warning-500" />
                  <span className="text-base font-semibold text-darkgray-900">
                    {t("chatbot-import-adjust-section")}
                  </span>
                  <span className="rounded-full bg-warning-500 px-2.5 py-0.5 text-xs font-medium text-white tracking-[-0.072px] flex items-center justify-center">
                    {invalidEmails.length}
                  </span>
                </div>
                {showInvalid ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {showInvalid && (
                <div className="max-h-[min(50vh,320px)] min-h-0 overflow-auto">
                  <div className="space-y-4 px-4 pb-4">
                    {/* Duplicados */}
                    {duplicateErrors.length > 0 && (
                      <InvalidSubsection
                        title={t(REASON_KEYS.duplicado)}
                        items={duplicateErrors}
                        selectedIds={selectedIds}
                        isAllSelected={isSubsectionAllSelected(duplicateErrors)}
                        onToggleAll={() =>
                          toggleSelectAll(duplicateErrors.map((e) => e.id))
                        }
                        onToggleOne={toggleSelectOne}
                        editingSubsection={editingSubsection}
                        editValues={editValues}
                        onEditValueChange={(id, v) =>
                          setEditValues((prev) => ({ ...prev, [id]: v }))
                        }
                        onStartEdit={() => startEditSubsection("duplicado")}
                        onSaveEdit={() => saveEditSubsection("duplicado")}
                        onSaveOne={saveEditOneInvalid}
                        onCancelOne={cancelEditOneInvalid}
                        onCancelEdit={cancelEditSubsection}
                        onIgnore={() =>
                          handleIgnoreSelected(
                            duplicateErrors
                              .filter((e) => selectedIds.has(e.id))
                              .map((e) => e.id),
                          )
                        }
                        onAutocorrect={() =>
                          handleAutocorrect(
                            "duplicado",
                            duplicateErrors
                              .filter((e) => selectedIds.has(e.id))
                              .map((e) => e.id),
                          )
                        }
                        disabled={confirmed}
                        isLast={lastInvalidReason === "duplicado"}
                      />
                    )}

                    {/* Formato inválido */}
                    {formatErrors.length > 0 && (
                      <InvalidSubsection
                        title={t(REASON_KEYS.formato_invalido)}
                        items={formatErrors}
                        selectedIds={selectedIds}
                        isAllSelected={isSubsectionAllSelected(formatErrors)}
                        onToggleAll={() =>
                          toggleSelectAll(formatErrors.map((e) => e.id))
                        }
                        onToggleOne={toggleSelectOne}
                        editingSubsection={editingSubsection}
                        editValues={editValues}
                        onEditValueChange={(id, v) =>
                          setEditValues((prev) => ({ ...prev, [id]: v }))
                        }
                        onStartEdit={() =>
                          startEditSubsection("formato_invalido")
                        }
                        onSaveEdit={() =>
                          saveEditSubsection("formato_invalido")
                        }
                        onSaveOne={saveEditOneInvalid}
                        onCancelOne={cancelEditOneInvalid}
                        onCancelEdit={cancelEditSubsection}
                        onIgnore={() =>
                          handleIgnoreSelected(
                            formatErrors
                              .filter((e) => selectedIds.has(e.id))
                              .map((e) => e.id),
                          )
                        }
                        onAutocorrect={() =>
                          handleAutocorrect(
                            "formato_invalido",
                            formatErrors
                              .filter((e) => selectedIds.has(e.id))
                              .map((e) => e.id),
                          )
                        }
                        disabled={confirmed}
                        isLast={lastInvalidReason === "formato_invalido"}
                      />
                    )}

                    {/* Domínio não permitido */}
                    {domainErrors.length > 0 && (
                      <InvalidSubsection
                        title={t(REASON_KEYS.dominio_invalido)}
                        items={domainErrors}
                        selectedIds={selectedIds}
                        isAllSelected={isSubsectionAllSelected(domainErrors)}
                        onToggleAll={() =>
                          toggleSelectAll(domainErrors.map((e) => e.id))
                        }
                        onToggleOne={toggleSelectOne}
                        editingSubsection={editingSubsection}
                        editValues={editValues}
                        onEditValueChange={(id, v) =>
                          setEditValues((prev) => ({ ...prev, [id]: v }))
                        }
                        onStartEdit={() =>
                          startEditSubsection("dominio_invalido")
                        }
                        onSaveEdit={() =>
                          saveEditSubsection("dominio_invalido")
                        }
                        onSaveOne={saveEditOneInvalid}
                        onCancelOne={cancelEditOneInvalid}
                        onCancelEdit={cancelEditSubsection}
                        onIgnore={() =>
                          handleIgnoreSelected(
                            domainErrors
                              .filter((e) => selectedIds.has(e.id))
                              .map((e) => e.id),
                          )
                        }
                        onAutocorrect={() =>
                          handleAutocorrect(
                            "dominio_invalido",
                            domainErrors
                              .filter((e) => selectedIds.has(e.id))
                              .map((e) => e.id),
                          )
                        }
                        disabled={confirmed}
                        allowedDomains={allowedDomains}
                        isLast={lastInvalidReason === "dominio_invalido"}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {validEmails.length === 0 && invalidEmails.length > 0 && (
          <div className="px-4 py-3">
            <p className="text-xs text-muted-foreground">
              {t("chatbot-import-select-one-edit")}
            </p>
          </div>
        )}
      </div>
      {/* Ações globais */}
      {validEmails.length > 0 && (
        <div className="flex gap-2 pb-4 w-full overflow-hidden">
          <Button
            variant="outline-aiBlue"
            size="sm"
            disabled={confirmed}
            className="h-9 flex-1 gap-1.5 text-sm w-full rounded-[10px]"
            onClick={() => handleAction("remove")}
          >
            <UserRoundMinus className="h-4 w-4" />
            {t("chatbot-import-remove-members")}
          </Button>
          <Button
            size="sm"
            disabled={confirmed}
            className="h-9 flex-1 gap-1.5 bg-aiBlue-500 text-sm text-white hover:bg-aiBlue-700 w-full rounded-[10px]"
            onClick={() => handleAction("invite")}
          >
            <UserRoundPlus className="h-4 w-4" />
            {t("chatbot-import-add-members")}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ---------- SelectAllRow helper ---------- */

function SelectAllRow({
  totalCount,
  selectedCount,
  onToggle,
  disabled,
  label,
  showTotalBadge,
  badgeColor,
}: {
  totalCount: number;
  selectedCount: number;
  onToggle: () => void;
  disabled?: boolean;
  label?: string;
  showTotalBadge?: boolean;
  badgeColor?: "success" | "warning";
}) {
  const hasSelection = selectedCount > 0;
  const allSelected = selectedCount === totalCount;
  const badgeBg =
    badgeColor === "warning"
      ? "bg-warning-500/15 text-warning-600"
      : "bg-success-500/15 text-success-600";

  return (
    <div className="flex items-center gap-2 bg-lightgray-200 rounded-t-xl border border-b-0 p-4">
      <button
        type="button"
        role="checkbox"
        aria-checked={allSelected ? "true" : hasSelection ? "mixed" : "false"}
        disabled={disabled}
        onClick={onToggle}
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors",
          hasSelection
            ? "border-neutral-900 bg-neutral-900 text-white"
            : "border-primary bg-transparent",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        {allSelected && <Check className="h-3 w-3" />}
        {hasSelection && !allSelected && <Minus className="h-3 w-3" />}
      </button>
      <span className="cursor-pointer text-base font-medium text-darkgray-900">
        {label ?? "Selecionar todos"}
      </span>
      {showTotalBadge && (
        <span
          className={cn(
            "rounded-full px-2.5 py-0.5 text-xs font-medium",
            badgeBg,
          )}
        >
          {totalCount}
        </span>
      )}
      {hasSelection && !showTotalBadge && (
        <span className="text-xs font-medium text-darkgray-900 tracking-[-0.072px] flex items-center justify-center px-2.5 py-0.5">
          {selectedCount}
        </span>
      )}
    </div>
  );
}

/* ---------- Subsection: Duplicados / Formato inválido / Domínio ---------- */

interface InvalidSubsectionProps {
  title: string;
  items: InvalidWithId[];
  selectedIds: Set<number>;
  isAllSelected?: boolean;
  onToggleAll: () => void;
  onToggleOne: (id: number) => void;
  editingSubsection: InvalidEmail["reason"] | null;
  editValues: Record<number, string>;
  onEditValueChange: (id: number, value: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onSaveOne: (id: number) => void;
  onCancelOne: (id: number) => void;
  onCancelEdit: () => void;
  onIgnore: () => void;
  onAutocorrect: () => void;
  disabled?: boolean;
  allowedDomains?: string[];
  isLast?: boolean;
}

function InvalidSubsection({
  title,
  items,
  selectedIds,
  onToggleAll,
  onToggleOne,
  editingSubsection,
  editValues,
  onEditValueChange,
  onStartEdit,
  onSaveEdit,
  onSaveOne,
  onCancelOne,
  onCancelEdit,
  onIgnore,
  onAutocorrect,
  disabled = false,
  allowedDomains = [],
  isLast = false,
}: InvalidSubsectionProps) {
  const [t] = useTranslation(["core"]);
  const reason = items[0]?.reason ?? "formato_invalido";
  const isEditing = editingSubsection === reason;
  const selectedCount = items.filter((e) => selectedIds.has(e.id)).length;
  const [showNoSelectionWarning, setShowNoSelectionWarning] =
    React.useState(false);

  React.useEffect(() => {
    if (selectedCount > 0) setShowNoSelectionWarning(false);
  }, [selectedCount]);

  return (
    <div className="pt-2">
      <SelectAllRow
        totalCount={items.length}
        selectedCount={selectedCount}
        onToggle={onToggleAll}
        disabled={disabled}
        label={title}
        showTotalBadge
        badgeColor="warning"
      />
      <div className="overflow-hidden border rounded-b-xl mt-0 mb-2">
        {items.map((item, idx) => {
          const isSelected = selectedIds.has(item.id);
          const prevSelected = idx > 0 && selectedIds.has(items[idx - 1].id);
          const isEditRow = isEditing && isSelected;
          return (
            <div
              key={item.id}
              className={cn(
                "flex items-center gap-2 px-4 py-3 transition-colors",
                isSelected ? "bg-aiBlue-100" : "bg-white",
                idx > 0 && !isSelected && !prevSelected
                  ? "border-t border-border"
                  : idx > 0
                    ? "border-t border-transparent"
                    : "",
              )}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleOne(item.id)}
                disabled={disabled}
                className="shrink-0 rounded data-[state=checked]:bg-neutral-900 data-[state=checked]:border-neutral-900 data-[state=checked]:text-white"
              />
              {isEditRow ? (
                <>
                  <Input
                    value={editValues[item.id] ?? item.email}
                    onChange={(e) => onEditValueChange(item.id, e.target.value)}
                    className="h-8 flex-1 border border-border bg-white text-xs"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSaveOne(item.id);
                      if (e.key === "Escape") onCancelOne(item.id);
                    }}
                  />
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-foreground hover:bg-aiBlue-200"
                      onClick={() => onSaveOne(item.id)}
                      disabled={disabled}
                      aria-label={String(t("chatbot-import-confirm"))}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:bg-danger-500/10 hover:text-danger-500"
                      onClick={() => onCancelOne(item.id)}
                      disabled={disabled}
                      aria-label={String(t("chatbot-import-undo"))}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </>
              ) : (
                <span className="min-w-0 flex-1 truncate text-sm text-darkgray-900">
                  {item.email}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {allowedDomains.length > 0 && (
        <p className="text-[10px] text-muted-foreground mb-4">
          {t("chatbot-import-domains-accepted")}{" "}
          {allowedDomains.map((d, i) => (
            <span key={d}>
              <code className="rounded bg-muted px-1 py-0.5">{d}</code>
              {i < allowedDomains.length - 1 && ", "}
            </span>
          ))}
        </p>
      )}
      {showNoSelectionWarning && (
        <p className="text-xs text-danger-500 mb-4 mt-2">
          {t("chatbot-import-select-one")}
        </p>
      )}
      <div
        className={cn(
          "grid grid-cols-3 gap-2 pt-2",
          isLast ? "pb-0" : "pb-6 border-b border-border",
        )}
      >
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            "h-8 gap-1 text-sm rounded-[10px]",
            isEditing
              ? "hover:bg-danger-500/10 text-muted-foreground hover:text-danger-500"
              : "hover:bg-white text-aiBlue-500 hover:text-darkgray-900",
          )}
          onClick={() => {
            if (isEditing) {
              onCancelEdit();
              return;
            }
            if (selectedCount === 0) {
              setShowNoSelectionWarning(true);
              return;
            }
            onStartEdit();
          }}
        >
          {isEditing ? (
            <X className="h-4 w-4" />
          ) : (
            <Pencil className="h-4 w-4" />
          )}
          {isEditing ? t("chatbot-import-cancel") : t("chatbot-import-edit")}
        </Button>
        <Button
          variant="outline-aiBlue"
          size="sm"
          disabled={disabled}
          className="h-8 gap-1 text-sm rounded-[10px]"
          onClick={() => {
            if (selectedCount === 0) {
              setShowNoSelectionWarning(true);
              return;
            }
            onIgnore();
          }}
        >
          <Trash2 className="h-4 w-4" />
          {t("chatbot-import-ignore")}
        </Button>
        <Button
          size="sm"
          disabled={disabled}
          className="h-8 gap-1 bg-aiBlue-500 text-sm text-white hover:bg-aiBlue-700 rounded-[10px]"
          onClick={() => {
            if (selectedCount === 0) {
              setShowNoSelectionWarning(true);
              return;
            }
            onAutocorrect();
          }}
        >
          <Sparkles className="h-4 w-4" />
          {t("chatbot-import-autocorrect")}
        </Button>
        {isEditing && (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            className="h-8 text-sm rounded-[10px]"
            onClick={onCancelEdit}
          >
            {t("chatbot-import-cancel")}
          </Button>
        )}
      </div>
    </div>
  );
}
